import { zodResolver } from '@hookform/resolvers/zod'
import { cn } from '@repo/tailwind-config/tailwind.functions'
import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import Badge from '@repo/ui/badge'
import Typography from '@repo/ui/typography'
import { useQuery } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'
import { z, ZodType } from 'zod'

import { getTefls } from '../../../../../api/tefl/get-tefls'
import useTranslate from '../../../../../lib/hooks/use-translate'
import {
  TeflTypeEnum,
  teflTypeEnumMapping
} from '../../../../../lib/types/enums/ol/tefl-type.enum'
import { CenterTeflInput } from '../../../../../lib/types/models/center-tefl/center-tefl-input'
import { TeflProperties } from '../../../../../lib/types/models/tefl/properties.tefl'
import { BaseFormProps } from '../../../../../lib/types/utils'
import { getSubmitAction } from '../../../../../lib/utils/buttons-config/validation-buttons-config'
import { getSelectedOption } from '../../../../../lib/utils/functions/misc.functions'
import { createNumberSchema } from '../../../../../lib/utils/validations/int-array-validation'
import { FORM_MODE } from '../../../../../utils/constants/form.constants'
import { Form, FormControl, FormField, FormItem } from '../../../../elements/forms/form'
import ControlledSelectInput from '../../../../elements/inputs/controlled-select-input'
import ValidationButtons from '../../../../elements/validation-buttons'

type Props = BaseFormProps<CenterTeflInput, true> & {
  className?: string
  olId: number
  qualification: TeflTypeEnum
}

const formSchema: ZodType<CenterTeflInput> = z.object({
  id: createNumberSchema('id'),
  teflId: createNumberSchema('tefl')
})

const CenterTeflForm = ({
  className,
  olId,
  qualification,
  defaultValues,
  ...validationButtonProps
}: Props) => {
  const { t } = useTranslate()
  const form = useForm<CenterTeflInput>({
    resolver: zodResolver(formSchema),
    defaultValues,
    mode: FORM_MODE
  })

  const { data: tefls } = useQuery({
    queryKey: ['tefls'],
    queryFn: () => {
      return getTefls(olId)
    },
    enabled: !!olId
  })

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(getSubmitAction(validationButtonProps.buttonsConfig))}>
        <div className={cn('flex flex-col gap-4', className)}>
          <div className="flex items-center gap-1">
            <Typography variant="label-medium" color={tailwindColors.black.DEFAULT}>
              Qualification :
            </Typography>
            <Badge text={t(teflTypeEnumMapping[qualification])} />
          </div>
          <FormField
            control={form.control}
            name={'teflId'}
            render={({ field }) => {
              const selectedTefl = tefls?.data.find((tefl) => tefl.id === field.value)
              return (
                <FormItem className={'flex-1'}>
                  <FormControl>
                    <ControlledSelectInput
                      {...field}
                      required
                      dropDownPosition="above"
                      label={'Add TEFL'}
                      options={tefls?.data.map((tefl) => ({
                        label: TeflProperties.teflLabel(tefl),
                        value: tefl.id
                      }))}
                      selectedOption={getSelectedOption(
                        field.value,
                        selectedTefl ? TeflProperties.teflLabel(selectedTefl) : undefined
                      )}
                    />
                  </FormControl>
                </FormItem>
              )
            }}
          />
        </div>

        <ValidationButtons {...validationButtonProps} hasBorderTop />
      </form>
    </Form>
  )
}

export default CenterTeflForm
